"use client";

import { MentionProp } from "@/types/post";
import BaseEditor from "@/components/common/editors/base-editor";
import EditorMediaAndLinksPreview from "@/components/common/editors/base-editor/components/editor-media-and-links-preview";
import ProfilePicture from "@/components/common/profile-picture";
import { Button } from "@/components/new/button";
import { POSTS_API } from "@/lib/apis";
import { formatImageUrl } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useCustomEditor } from "@/providers/custom-editor-provider";
import { useModal } from "@/providers/modal-provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { JSONContent } from "@tiptap/react";
import { Loader2Icon, X } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { ModalType } from "@/types/modal-type";

type PostCommentInputFieldProps = {
	postSlug: string;
	parentCommentId?: string;
	onSubmit?: () => void;
};

const PostCommentInputField = ({
	postSlug,
	parentCommentId,
	onSubmit,
}: PostCommentInputFieldProps) => {
	const queryClient = useQueryClient();
	const { isLoggedIn, user } = useAuth();
	const { openModal } = useModal();

	const { images, setImages, clearContent } = useCustomEditor();

	const profileImageUrl = formatImageUrl(user?.image);
	const isVerified = user?.isVerified || false;
	const isFounder = user?.isFounder || false;

	const clearFormState = {
		htmlContent: "",
		jsonContent: {} as JSONContent,
		mentions: [] as MentionProp[],
	};

	const [form, setForm] = useState(clearFormState);
	const [isExpanded, setIsExpanded] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const handleContentChange = ({
		html,
		json,
		mentions = [],
	}: {
		html: string;
		json: JSONContent;
		mentions?: MentionProp[];
	}) => {
		setForm({
			htmlContent: html,
			jsonContent: json,
			mentions,
		});

		setErrorMessage("");
	};

	const handleFocus = () => {
		if (!isLoggedIn) {
			openModal(ModalType.Auth);
			return;
		}
		setIsExpanded(true);
	};

	const handleClose = () => {
		setIsExpanded(false);
		setForm(clearFormState);
		clearContent();
		setErrorMessage("");
	};

	const postComment = async () => {
		if (!isLoggedIn) {
			openModal(ModalType.Auth);
			return;
		}

		setIsLoading(true);

		const minCommentLength = 5;
		const maxCommentLength = 2000;
		const trimmedContent = form.htmlContent.replace(/<p>|<\/p>/g, "").trim();

		if (!trimmedContent) {
			setErrorMessage("Please enter a comment");
			setIsLoading(false);
			return;
		}

		if (trimmedContent.length < minCommentLength || trimmedContent.length > maxCommentLength) {
			setErrorMessage(
				`Comment should be between ${minCommentLength} and ${maxCommentLength} characters`
			);

			setIsLoading(false);
			return;
		}

		try {
			const url = new URL(`${POSTS_API}/${postSlug}/comments`);

			const body: Record<string, unknown> = {
				htmlContent: form.htmlContent,
				jsonContent: form.jsonContent,
				mentions: form.mentions,
				images: images,
			};

			if (parentCommentId) {
				body["parentCommentId"] = parentCommentId;
			}

			const response = await fetch(url, {
				method: "POST",
				body: JSON.stringify(body),
			});

			const data = await response.json();
			const message = data?.message;

			if (response.ok) {
				setForm({
					htmlContent: "",
					jsonContent: {},
					mentions: [],
				});

				setImages([]);
				setIsExpanded(false);
				toast.success(message);
			} else {
				toast.error(message);
			}

			if (onSubmit) {
				onSubmit();
			}
		} catch (error) {
			console.error("Error occurred while posting the comment", error);
		} finally {
			setIsLoading(false);
		}
	};

	const postCommentMutation = useMutation({
		mutationFn: postComment,
		onSuccess: () => {
			clearContent();
			return queryClient.invalidateQueries({ queryKey: ["post-comments", postSlug] });
		},
	});

	return (
		<div
			className={`mb-4 flex w-full flex-col gap-3 rounded-md border bg-white shadow-sm transition-all duration-300 ease-in-out focus-within:border-blue-500 ${
				isExpanded ? "h-auto p-3" : "h-[50px] overflow-hidden px-2 py-0"
			}`}
		>
			{!isExpanded ? (
				<div className="flex h-full w-full items-center gap-2">
					<ProfilePicture
						isFounder={isFounder}
						isVerified={isVerified}
						profilePictureUrl={profileImageUrl}
						className={{
							container: "h-8 w-8 rounded-full p-0.5",
							image: "rounded-full text-gray-600",
							badgeImage: "-bottom-2 h-4 w-4 md:h-5 md:w-5",
						}}
					/>
					<div
						className="flex h-full flex-1 cursor-text items-center text-sm text-gray-500"
						onClick={handleFocus}
					>
						{isLoggedIn ? "Add your comment..." : "Sign in to comment..."}
					</div>
					<Button
						type="button"
						onClick={handleFocus}
						size="sm"
						className="bg-brand-primary text-xs font-medium text-white hover:bg-brand-primary/90"
					>
						Comment
					</Button>
				</div>
			) : (
				<>
					<div className="flex items-center justify-between border-b pb-3">
						<div className="flex items-center gap-3">
							<ProfilePicture
								isFounder={isFounder}
								isVerified={isVerified}
								profilePictureUrl={profileImageUrl}
								className={{
									container: "h-10 w-10 rounded-full p-0.5",
									image: "rounded-full text-gray-600",
									badgeImage: "-bottom-3 h-5 w-5 md:h-6 md:w-6",
								}}
							/>
							<div>
								<h5 className="text-sm font-medium">{user?.name || "User"}</h5>
							</div>
						</div>

						<Button
							onClick={handleClose}
							variant="ghost"
							size="icon"
							className="h-9 w-9 rounded-full"
							aria-label="Close comment editor"
						>
							<X size={16} />
						</Button>
					</div>

					<BaseEditor
						content={form.htmlContent}
						handleOnUpdate={handleContentChange}
						placeholder="Add your comment"
						classNames={{
							editor: "focus-visible:outline-none min-h-[100px]",
						}}
					/>

					<EditorMediaAndLinksPreview />
					{errorMessage && <span className="text-xs text-red-400">{errorMessage}</span>}

					<div className="mt-2 flex justify-end">
						<Button
							type="button"
							disabled={isLoading}
							onClick={() => postCommentMutation.mutate()}
							className="bg-brand-primary text-sm font-semibold text-white hover:bg-brand-primary/90"
						>
							{isLoading ? (
								<Loader2Icon className="mr-2 h-4 w-4 animate-spin stroke-white" />
							) : null}
							Comment
						</Button>
					</div>
				</>
			)}
		</div>
	);
};

export default PostCommentInputField;
